import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Tag } from '../ui/atoms/Tag';
import { Button } from '@/components/ui/atoms/Button';
import { usePsychometricTestDetail } from '../../hooks/usePsychometricTests';
import {
  resolvePsychometricTestDescription,
  resolvePsychometricTestName,
  type JobTFunction,
} from '../../utils/psychometric-test-copy';
import type { Locale } from '@/i18n/config';
import type { ExamLanguage } from '@/types/exam-config.types';
import { examLanguageToLocale, getFixedTForExamLanguage } from '@/utils/locale';

/** Resolve a multilingual value (dict or string) to a flat string for the given locale. */
const resolveText = (val: unknown, locale: Locale): string => {
  if (val && typeof val === 'object' && !Array.isArray(val)) {
    const dict = val as Record<string, string>;
    return dict[locale] ?? dict.en ?? '';
  }
  return typeof val === 'string' ? val : '';
};

interface PreviewModalProps {
  testId: string | null;
  language: ExamLanguage | string;
  onClose: () => void;
}

export const PreviewModal = ({ testId, language, onClose }: PreviewModalProps) => {
  const { t } = useTranslation('job');
  const { data: detail, isLoading } = usePsychometricTestDetail(testId);
  const contentLang = examLanguageToLocale(language);
  const tExam = useMemo<JobTFunction>(
    () => getFixedTForExamLanguage(language),
    [language],
  );
  const previewName = detail
    ? resolvePsychometricTestName(detail.slug, detail.name, tExam)
    : t('exam_config.preview.title_fallback');
  const previewDescription = detail
    ? resolvePsychometricTestDescription(detail.slug, detail.description, tExam)
    : null;

  return (
    <Dialog open={!!testId} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <DialogTitle className="text-base font-heading font-semibold text-dark-invirtus pr-8">
              {isLoading ? t('exam_config.preview.loading') : previewName}
            </DialogTitle>
            <Button
              type="icon"
              intent="muted"
              size="sm"
              icon={<X/>}
              onClick={onClose}
              aria-label={t('exam_config.actions.close')}
            />
          </div>
        </DialogHeader>

        {isLoading && (
          <div className="flex-1 flex items-center justify-center py-12">
            <span className="text-sm font-body text-dark-invirtus/40">{t('exam_config.preview.loading_questions')}</span>
          </div>
        )}

        {!isLoading && detail && (
          <div className="flex flex-col gap-4 overflow-hidden">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-xs font-body text-dark-invirtus/50">
                {previewDescription}
              </span>
              <div className="flex items-center gap-2">
                <Tag intent="primary" variant="subtle">
                  {t('exam_config.preview.questions_count', { count: detail.question_count })}
                </Tag>
                <Tag intent="info" variant="subtle" icon={<Clock />}>
                  {t('exam_config.library.estimated_minutes', { minutes: detail.estimated_minutes })}
                </Tag>
                <Tag intent="primary" variant="subtle">
                  {t('exam_config.preview.version', { version: detail.version })}
                </Tag>
              </div>
            </div>

            <div className="overflow-y-auto flex-1 space-y-2 pr-1">
              {detail.questions_data.map((q: { id: number; text?: unknown; left_text?: string; right_text?: string; scale_labels?: { left?: unknown; right?: unknown }; dimension: string }, index: number) => {
                const text = resolveText(q.text, contentLang);
                const leftText = q.left_text ?? resolveText(q.scale_labels?.left, contentLang);
                const rightText = q.right_text ?? resolveText(q.scale_labels?.right, contentLang);

                return (
                  <div
                    key={q.id}
                    className="px-4 py-3 rounded-lg border border-dark-invirtus/8 bg-white"
                  >
                    <div className="flex items-start gap-3">
                      <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-dark-invirtus/5 text-[11px] font-heading font-semibold text-dark-invirtus/50">
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-body text-dark-invirtus leading-relaxed">{text}</p>
                        {leftText && rightText && (
                          <div className="mt-1.5 flex items-center gap-2 text-[11px] font-body text-dark-invirtus/40">
                            <span className="px-2 py-0.5 rounded bg-dark-invirtus/4 border border-dark-invirtus/8">{leftText}</span>
                            <span className="text-dark-invirtus/25">&harr;</span>
                            <span className="px-2 py-0.5 rounded bg-dark-invirtus/4 border border-dark-invirtus/8">{rightText}</span>
                          </div>
                        )}
                      </div>
                      <Tag intent="primary" variant="subtle" className="shrink-0">
                        {q.dimension}
                      </Tag>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
